/**
 * Bubble stream hook - replaces useExecutionStream + useQuickCreateRun.
 * Provides timeline-based streaming with discrete bubbles.
 * Now uses global streaming store for state persistence across navigation.
 *
 * Type flow: SSE → AgentStreamEvent (SDK types) → StreamAction → Bubble (UI types)
 * SDK types reference: https://github.com/anthropics/claude-agent-sdk-python/blob/main/src/claude_agent_sdk/types.py
 */

import { useCallback, useEffect, useRef } from "react";
import { useVerificationModalStore } from "@/components/email-verification-modal";
import { openUpgradeModalFromError } from "@/components/upgrade-modal";
import { env } from "@/env";
import { getQueryClient } from "@/integrations/tanstack-query/root-provider";
import { isOnCooldown, sendVerificationEmail } from "@/lib/api/resend-verification";
import type { components } from "@/lib/api/types";
import { DEFAULT_PERMISSION_TIMEOUT_SECONDS } from "@/lib/streaming/constants";
import { createMockResponse, loadMockScript } from "@/lib/streaming/mock";
import { isMockMode } from "@/lib/streaming/mode";
import { streamFromResponse } from "@/lib/streaming/parser";
import type { AgentStreamEvent } from "@/lib/streaming/types";
import { EventType } from "@/lib/streaming/types";
import type { ContentBlock } from "@/lib/tools/schemas";
import { useAuthStore } from "@/stores/auth-store";
import { useStreamingStore } from "@/stores/streaming-store";
import type { Bubble, StreamAction } from "./reducer";

type QuickCreateRequestBody = components["schemas"]["QuickCreateTaskRequest"];

export interface BubbleStreamOptions {
	instructions: string;
	requestBody: QuickCreateRequestBody;
	accessToken: string;
	runUuid?: string;
	runId?: number;
	files?: File[];
}

export interface ChatStreamOptions {
	instanceId: number;
	message: string;
	runUuid?: string;
	runId?: number;
	accessToken: string;
	model?: string;
	maxTurns?: number;
	permissionMode?: "yolo" | "safe" | "plan";
	integrationIds?: number[];
	files?: File[];
	historyBubbles?: Bubble[];
	historyMetadata?: {
		runId?: number;
		agentName?: string;
		runUuid?: string;
	};
}

export interface JoinStreamOptions {
	runId: number;
	accessToken: string;
	historyBubbles?: Bubble[];
	historyMetadata?: {
		runId?: number;
		agentName?: string;
		runUuid?: string;
	};
}

export interface BubbleStreamState {
	bubbles: Bubble[];
	agentName: string | null;
	runUuid: string | null;
	runId: number | null;
	isStreaming: boolean;
	error: Error | null;
	start: (options: BubbleStreamOptions) => Promise<void>;
	startChat: (options: ChatStreamOptions) => Promise<void>;
	join: (options: JoinStreamOptions) => Promise<void>;
	stop: (options?: { cancelRun?: boolean }) => Promise<void>;
	reset: () => void;
}

// Track unknown event types to avoid spamming console (only log each type once)
const loggedUnknownEvents = new Set<string>();

/**
 * Check response and throw error with status code if not OK.
 */
async function throwIfNotOk(response: Response): Promise<void> {
	if (response.ok) return;
	const errorData = await response.json().catch(() => ({}));
	const detail =
		typeof errorData.detail === "string"
			? errorData.detail
			: `HTTP ${response.status}: ${response.statusText}`;
	const error = new Error(detail);
	(error as Error & { statusCode?: number }).statusCode = response.status;
	(error as Error & { data?: Record<string, unknown> }).data = errorData;
	throw error;
}

/**
 * Map SSE events to reducer actions
 */
function eventToAction(event: AgentStreamEvent): StreamAction | null {
	const eventType = (event as { type?: string }).type;
	const eventSubtype = (event as { subtype?: string }).subtype;

	// Ignore internal SDK events that use 'subtype' instead of 'type'
	if (eventSubtype === "init" || eventSubtype === "success") {
		return null;
	}

	if (eventType === "unknown") {
		return null;
	}

	if (!eventType) {
		return null;
	}

	// Handle agent_config_updated event (not in EventType enum)
	if (eventType === "agent_config_updated") {
		const queryClient = getQueryClient();
		if (queryClient) {
			const agentId = (event as { agent_id?: number }).agent_id;
			const taskId = (event as { task_id?: number }).task_id;
			queryClient.invalidateQueries({ queryKey: ["agents"] });
			if (agentId) {
				queryClient.invalidateQueries({ queryKey: ["agent", agentId] });
			}
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
			if (taskId) {
				queryClient.invalidateQueries({ queryKey: ["task", taskId] });
			}
		}

		const agentName = (event as { agent?: { name?: string } }).agent?.name;
		if (agentName) {
			return {
				type: "METADATA",
				data: { agentName },
			};
		}
		return null;
	}

	switch (event.type) {
		case EventType.ContentBlockStart: {
			// Support both new and legacy event formats for forward compatibility
			// New format: { id: "block_0", block_type: "text" }
			// Legacy format (current backend): { index: 0, content_block: { type: "text", text: "" } }

			// Fix 10: Extract and preserve event metadata
			const eventMetadata: Record<string, unknown> = {};
			if ("metadata" in event && typeof event.metadata === "object" && event.metadata !== null) {
				Object.assign(eventMetadata, event.metadata);
			}

			if ("id" in event && "block_type" in event) {
				const blockType = event.block_type as string; // "text" or "tool_use"
				const blockId = event.id as string;

				return {
					type: "BLOCK_START",
					blockType,
					blockId,
					metadata: eventMetadata,
				};
			}

			// Legacy format fallback
			if ("index" in event && "content_block" in event) {
				const eventWithBlock = event as {
					index: number;
					content_block: { type: string; id?: string };
				};
				const contentBlock = eventWithBlock.content_block;
				const blockType = contentBlock.type; // "text" or "tool_use"
				const blockIndex = typeof eventWithBlock.index === "number" ? eventWithBlock.index : 0;
				// Use globally unique fallback ID (counter-based) to prevent cross-turn collisions.
				// Text blocks lack natural IDs, and block indices reset per message turn.
				const blockId =
					typeof contentBlock.id === "string"
						? contentBlock.id
						: `block-${eventToActionBlockSequence.value++}`;
				blockIndexToId.set(blockIndex, blockId);

				// Include position metadata for MESSAGE_SNAPSHOT dedup bridge
				eventMetadata.blockIndex = blockIndex;
				if (currentMessageId) {
					eventMetadata.messageId = currentMessageId;
				}

				return {
					type: "BLOCK_START",
					blockType,
					blockId,
					metadata: eventMetadata,
				};
			}

			return null;
		}

		case EventType.ContentBlockDelta:
		case EventType.MessageDelta: {
			// Extract text delta
			let delta = "";
			if (
				"delta" in event &&
				typeof event.delta === "object" &&
				event.delta &&
				"text" in event.delta &&
				typeof event.delta.text === "string"
			) {
				delta = event.delta.text;
			}

			// Extract input_json_delta for tool calls
			if (
				"delta" in event &&
				typeof event.delta === "object" &&
				event.delta &&
				"partial_json" in event.delta &&
				typeof event.delta.partial_json === "string"
			) {
				delta = event.delta.partial_json;
			}

			return delta ? { type: "BLOCK_DELTA", delta } : null;
		}

		case EventType.ContentBlockStop: {
			const rawId = (event as { id?: unknown }).id;
			const rawIndex = (event as { index?: unknown }).index;
			// Look up the unique block ID assigned during BLOCK_START (handles cross-turn collisions)
			const blockId =
				typeof rawId === "string"
					? rawId
					: typeof rawIndex === "number"
						? (blockIndexToId.get(rawIndex) ?? `block-${rawIndex}`)
						: undefined;
			return { type: "BLOCK_STOP", blockId };
		}

		case EventType.ToolUse:
			if ("id" in event && "name" in event) {
				const toolId = event.id as string;
				const toolName = event.name as string;
				const rawInput = "input" in event ? event.input : event.input_text;
				// Ensure input is always a Record<string, unknown>
				const input: Record<string, unknown> =
					typeof rawInput === "object" && rawInput !== null
						? (rawInput as Record<string, unknown>)
						: typeof rawInput === "string"
							? { raw: rawInput }
							: {};

				return {
					type: "TOOL_USE",
					toolId,
					toolName,
					input,
				};
			}
			// WARN: Event missing required fields
			console.warn("[EventToAction] ToolUse event missing required fields (id/name):", event);
			return null;

		case EventType.ToolResult:
			if ("tool_use_id" in event && "content" in event) {
				const toolUseId = event.tool_use_id as string;
				const result =
					typeof event.content === "string" ? event.content : JSON.stringify(event.content);
				const isError = "is_error" in event ? (event.is_error as boolean | null) : null;
				return { type: "TOOL_RESULT", toolUseId, result, isError };
			}
			// WARN: Event missing required fields
			console.warn(
				"[EventToAction] ToolResult event missing required fields (tool_use_id/content):",
				event
			);
			return null;

		case EventType.PlanDelta:
			if ("text" in event && typeof event.text === "string") {
				return { type: "PLAN_DELTA", text: event.text };
			}
			return null;

		case EventType.Thinking:
			if ("content" in event && typeof event.content === "string") {
				const signature = "signature" in event ? (event.signature as string | null) : null;
				return { type: "THINKING", content: event.content, signature };
			}
			return null;

		case EventType.Reasoning:
			if ("content" in event && typeof event.content === "string") {
				return { type: "REASONING", content: event.content };
			}
			return null;

		case EventType.Error:
			if ("message" in event) {
				return { type: "ERROR", message: event.message as string };
			}
			return null;

		case EventType.Metadata:
			if ("run_id" in event || "agent_name" in event || "run_uuid" in event) {
				return {
					type: "METADATA",
					data: {
						runId: event.run_id as number | undefined,
						agentName: event.agent_name as string | undefined,
						runUuid: event.run_uuid as string | undefined,
						instanceId: event.instance_id as number | undefined,
						taskId: event.task_id as number | undefined,
						status: event.status as string | undefined,
					},
				};
			}
			return null;

		case EventType.MessageSnapshot:
			if ("content_blocks" in event && Array.isArray(event.content_blocks)) {
				const contentBlocks = event.content_blocks as ContentBlock[];
				return {
					type: "MESSAGE_SNAPSHOT",
					messageId: "message_id" in event ? (event.message_id as string | undefined) : undefined,
					role: "role" in event ? (event.role as string | undefined) : undefined,
					model: "model" in event ? (event.model as string | undefined) : undefined,
					usage:
						"usage" in event && typeof event.usage === "object"
							? (event.usage as Record<string, unknown>)
							: undefined,
					toolCalls:
						"tool_calls" in event && Array.isArray(event.tool_calls)
							? (event.tool_calls as Array<Record<string, unknown>>)
							: undefined,
					contentBlocks,
				};
			}
			return null;

		// SDK Lifecycle Events
		case EventType.SdkSuccess:
			return { type: "SDK_SUCCESS" };

		case EventType.SdkError:
			if ("error" in event && typeof event.error === "string") {
				return { type: "SDK_ERROR", message: event.error };
			}
			return null;

		case EventType.Cancelled:
			return { type: "CANCELLED" };

		// Sandbox Events - hidden from UI for now
		case EventType.SandboxConnecting:
		case EventType.SandboxConnected:
			return null;

		// Metrics Events (NEW schema from backend)
		case EventType.RunMetrics:
			if (
				"model" in event ||
				"execution_time_ms" in event ||
				"input_tokens_used" in event ||
				"output_tokens_used" in event ||
				"claude_token_cost_usd" in event ||
				"stop_reason" in event
			) {
				return {
					type: "RUN_METRICS",
					model: event.model as string | undefined,
					executionTimeMs: event.execution_time_ms as number | undefined,
					inputTokensUsed: event.input_tokens_used as number | undefined,
					outputTokensUsed: event.output_tokens_used as number | undefined,
					claudeTokenCostUsd: event.claude_token_cost_usd as number | undefined,
					stopReason: event.stop_reason as string | undefined,
				};
			}
			return null;

		// MCP Error Events
		case EventType.McpAuthFailed:
			if ("error" in event && typeof event.error === "string") {
				const source = "source" in event ? (event.source as string | undefined) : undefined;
				return {
					type: "MCP_AUTH_FAILED",
					message: event.error,
					source,
				};
			}
			return null;

		case EventType.McpError:
			if ("error" in event && typeof event.error === "string") {
				const source = "source" in event ? (event.source as string | undefined) : undefined;
				return {
					type: "MCP_ERROR",
					message: event.error,
					source,
				};
			}
			return null;

		// Compact boundary event (conversation summarization marker)
		case EventType.CompactBoundary: {
			const trigger = "trigger" in event ? (event.trigger as "manual" | "auto") : "auto";
			const preTokens = "pre_tokens" in event ? (event.pre_tokens as number) : 0;
			return {
				type: "COMPACT_BOUNDARY",
				trigger,
				preTokens,
			};
		}

		// Permission request event (tool approval required)
		case EventType.PermissionRequest: {
			const requestId =
				"request_id" in event && typeof event.request_id === "string"
					? event.request_id
					: undefined;
			const toolName =
				"tool_name" in event && typeof event.tool_name === "string" ? event.tool_name : undefined;

			if (!requestId || !toolName) {
				console.warn("[EventToAction] PermissionRequest missing required fields:", event);
				return null;
			}

			return {
				type: "PERMISSION_REQUEST",
				requestId,
				toolName,
				toolInput:
					"tool_input" in event && typeof event.tool_input === "object" && event.tool_input !== null
						? (event.tool_input as Record<string, unknown>)
						: {},
				timeoutSeconds:
					"timeout_seconds" in event && typeof event.timeout_seconds === "number"
						? event.timeout_seconds
						: DEFAULT_PERMISSION_TIMEOUT_SECONDS,
			};
		}

		case EventType.AwaitingApproval:
			return {
				type: "METADATA",
				data: {
					status: "awaiting_approval",
				},
			};

		// Track message boundaries for unique block ID generation
		case EventType.MessageStart: {
			if ("message" in event && typeof event.message === "object" && event.message) {
				currentMessageId = (event.message as { id?: string }).id;
			}
			if (!currentMessageId) {
				currentMessageId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
			}
			blockIndexToId.clear();
			return null;
		}

		// Ignore internal events (intentionally not handled in frontend)
		// - Message lifecycle events
		case EventType.MessageStop:
		case EventType.MessageComplete:
		// - Keep-alive and completion events
		case EventType.KeepAlive:
		case EventType.Done:
		// - SDK wrapper events (internal to agent-runtime)
		case EventType.SdkInit:
		case EventType.SdkContentSnapshot:
		// - Sandbox metrics (not displayed in UI currently)
		case EventType.SandboxMetrics:
		// - Stderr (logged in backend)
		case EventType.Stderr:
		// - Generic system message (handled via subtype, e.g. compact_boundary)
		case EventType.SystemMessage:
			return null;

		default: {
			// Fix 5: Always log unknown events (not just in DEV) to help with debugging
			// TypeScript thinks event is 'never' here since we've exhaustively handled all types
			// Use type assertion to access the type property
			const unknownEventType = (event as { type?: string }).type || "unknown";

			// Only log each unknown event type once to avoid spam
			if (!loggedUnknownEvents.has(unknownEventType)) {
				loggedUnknownEvents.add(unknownEventType);
				console.warn(`[Streaming] Unsupported event type received: "${unknownEventType}"`);
			}
			return null;
		}
	}
}

/**
 * Bubble stream hook - now reads/writes to global store
 */
const eventToActionBlockSequence = { value: 0 };
let currentMessageId: string | undefined;
const blockIndexToId = new Map<number, string>();
const resetBlockIdSequence = () => {
	eventToActionBlockSequence.value = 0;
	currentMessageId = undefined;
	blockIndexToId.clear();
};

type StreamHandlers = {
	onMetadata?: (runId?: number) => void;
	onError?: (err: Error) => void;
	onComplete?: () => void;
};

async function handleStreamError(
	err: unknown,
	{
		dispatch,
		setError,
		setStreaming,
		runIdRef,
		removeLocalRun,
		pendingLocalRunRef,
		abortControllerRef,
	}: {
		dispatch: (action: StreamAction) => void;
		setError: (err: Error | null) => void;
		setStreaming: (flag: boolean) => void;
		runIdRef: React.MutableRefObject<number | null>;
		removeLocalRun: (runId: number) => void;
		pendingLocalRunRef: React.MutableRefObject<boolean>;
		abortControllerRef?: React.MutableRefObject<AbortController | null>;
	}
) {
	const normalizedError = err instanceof Error ? err : new Error("Failed to start stream");
	const statusCode = (normalizedError as Error & { statusCode?: number }).statusCode;
	const errorData = (normalizedError as Error & { data?: Record<string, unknown> }).data;

	// Fix 1: Clear abortControllerRef on error to prevent memory leak
	if (abortControllerRef) {
		abortControllerRef.current = null;
	}

	// Special-case email verification required (403) — auto-send email + show modal
	if (statusCode === 403 && errorData?.error_code === "EMAIL_VERIFICATION_REQUIRED") {
		const runsRemaining = (errorData.runs_remaining as number) || 0;
		const email = useAuthStore.getState().user?.email;
		let emailSent = false;
		if (email && !isOnCooldown(email)) {
			const result = await sendVerificationEmail(email);
			emailSent = result.success;
		}
		useVerificationModalStore.getState().open(runsRemaining, emailSent);
		const currentRunId = runIdRef.current;
		if (currentRunId) {
			removeLocalRun(currentRunId);
		}
		setStreaming(false);
		pendingLocalRunRef.current = false;
		throw normalizedError;
	}

	// Special-case billing errors — open upgrade modal + add bubble
	if (statusCode === 402) {
		const typedData = (errorData ?? {}) as Record<string, unknown>;
		openUpgradeModalFromError(typedData);
		const errorCode = typedData.error_code as string | undefined;
		dispatch({
			type: "PAYMENT_ERROR",
			message: normalizedError.message,
			extra: {
				plan: typedData.plan as string | undefined,
				runs_used: typedData.runs_used as number | undefined,
				runs_limit: typedData.runs_limit as number | undefined,
				period_end: typedData.period_end as string | undefined,
				error_code: errorCode,
			},
		});
		const currentRunId = runIdRef.current;
		if (currentRunId) {
			removeLocalRun(currentRunId);
		}
		setStreaming(false);
		pendingLocalRunRef.current = false;
		return;
	}

	setError(normalizedError);
	const currentRunId = runIdRef.current;
	if (currentRunId) {
		removeLocalRun(currentRunId);
	}
	setStreaming(false);
	pendingLocalRunRef.current = false;
	throw normalizedError;
}

export async function runStream(
	response: Response,
	{
		dispatch,
		addLocalRun,
		removeLocalRun,
		setStreaming,
		runIdRef,
		pendingLocalRunRef,
		setError,
		handlers,
	}: {
		dispatch: (action: StreamAction) => void;
		addLocalRun: (runId: number) => void;
		removeLocalRun: (runId: number) => void;
		setStreaming: (val: boolean) => void;
		runIdRef: React.MutableRefObject<number | null>;
		pendingLocalRunRef: React.MutableRefObject<boolean>;
		setError: (err: Error | null) => void;
		handlers?: StreamHandlers;
	}
) {
	await streamFromResponse(
		response,
		(event) => {
			const action = eventToAction(event);
			if (action) {
				if (action.type === "METADATA" && pendingLocalRunRef.current && action.data.runId) {
					addLocalRun(action.data.runId);
					pendingLocalRunRef.current = false;
					// Invalidate user-runs immediately so navbar shows the new run
					const queryClient = getQueryClient();
					if (queryClient) {
						queryClient.invalidateQueries({ queryKey: ["user-runs"] });
					}
					if (handlers?.onMetadata) {
						handlers.onMetadata(action.data.runId);
					}
				}
				dispatch(action);
			}
		},
		(err) => {
			if (err.name !== "AbortError") {
				setError(err);
				handlers?.onError?.(err);
			}
			const currentRunId = runIdRef.current;
			if (currentRunId) {
				removeLocalRun(currentRunId);
			}
			setStreaming(false);
			pendingLocalRunRef.current = false;
		},
		() => {
			const currentRunId = runIdRef.current;
			if (currentRunId) {
				removeLocalRun(currentRunId);
			}
			setStreaming(false);
			pendingLocalRunRef.current = false;
			handlers?.onComplete?.();
		}
	);
}

export function useBubbleStream(): BubbleStreamState {
	// Use global store for state
	const bubbles = useStreamingStore((state) => state.bubbles);
	const isStreaming = useStreamingStore((state) => state.isStreaming);
	const error = useStreamingStore((state) => state.error);
	const runMetadata = useStreamingStore((state) => state.runMetadata);
	const dispatch = useStreamingStore((state) => state.dispatch);
	const setStreaming = useStreamingStore((state) => state.setStreaming);
	const setError = useStreamingStore((state) => state.setError);
	const reset = useStreamingStore((state) => state.reset);
	const hydrate = useStreamingStore((state) => state.hydrate);
	const addLocalRun = useStreamingStore((state) => state.addLocalRun);
	const removeLocalRun = useStreamingStore((state) => state.removeLocalRun);
	const setRunMetadata = useStreamingStore((state) => state.setRunMetadata);

	const abortControllerRef = useRef<AbortController | null>(null);
	const accessTokenRef = useRef<string | null>(null);
	const pendingLocalRunRef = useRef(false);
	const runIdRef = useRef<number | null>(runMetadata.runId ?? null);
	const isCleanedUpRef = useRef(false); // Fix 7: Prevent double cleanup

	useEffect(() => {
		runIdRef.current = runMetadata.runId ?? null;
	}, [runMetadata.runId]);

	const start = useCallback(
		async ({
			instructions,
			requestBody,
			accessToken,
			runUuid,
			runId,
			files,
		}: BubbleStreamOptions) => {
			if (!instructions.trim()) {
				throw new Error("Instructions are required");
			}
			if (!accessToken) {
				throw new Error("Not authenticated");
			}

			// Only reset if starting a NEW conversation
			// For follow-ups (runUuid exists), preserve history
			if (!runUuid) {
				reset();
				resetBlockIdSequence();
			}
			setError(null);
			setStreaming(true);
			pendingLocalRunRef.current = true;
			isCleanedUpRef.current = false; // Reset cleanup flag for new stream
			if (runId) {
				addLocalRun(runId);
				setRunMetadata({ runId, runUuid: runUuid ?? undefined });
				pendingLocalRunRef.current = false;
			}

			// Set user message
			dispatch({ type: "START_USER_MESSAGE", message: instructions });

			// Store access token for cancellation
			accessTokenRef.current = accessToken;

			try {
				// Mock mode
				if (isMockMode) {
					const script = await loadMockScript(env.VITE_AGENT_STREAM_FIXTURE_URL);
					const mockResponse = createMockResponse(script);

					await runStream(mockResponse, {
						dispatch,
						addLocalRun,
						removeLocalRun,
						setStreaming,
						runIdRef,
						pendingLocalRunRef,
						setError,
					});
					return;
				}

				// Real streaming
				const abortController = new AbortController();
				abortControllerRef.current = abortController;

				let response: Response;

				// Use FormData with multipart when files are present
				if (files && files.length > 0) {
					const formData = new FormData();
					formData.append("instructions", requestBody.instructions);
					if (requestBody.expected_output)
						formData.append("expected_output", requestBody.expected_output);
					if (requestBody.goals) formData.append("goals", requestBody.goals);
					if (requestBody.integration_ids) {
						for (const id of requestBody.integration_ids) {
							formData.append("integration_ids", String(id));
						}
					}
					formData.append("email_notifications", String(requestBody.email_notifications ?? false));
					formData.append("schedule", requestBody.schedule);
					if (requestBody.timezone) formData.append("timezone", requestBody.timezone);
					if (runUuid) formData.append("run_uuid", runUuid);
					if (runId) formData.append("run_id", String(runId));
					for (const file of files) {
						formData.append("files", file);
					}

					response = await fetch(`${env.VITE_API_BASE_URL}/api/v1/tasks/quick-create-with-files`, {
						method: "POST",
						headers: {
							Authorization: `Bearer ${accessToken}`,
							// Note: Don't set Content-Type for FormData - browser sets it with boundary
						},
						body: formData,
						signal: abortController.signal,
					});
				} else {
					// Standard JSON request without files
					const body = {
						...requestBody,
						...(runUuid && { run_uuid: runUuid }),
						...(runId && { run_id: runId }),
					};

					response = await fetch(`${env.VITE_API_BASE_URL}/api/v1/tasks/quick-create`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${accessToken}`,
						},
						body: JSON.stringify(body),
						signal: abortController.signal,
					});
				}

				await throwIfNotOk(response);

				await runStream(response, {
					dispatch,
					addLocalRun,
					removeLocalRun,
					setStreaming,
					runIdRef,
					pendingLocalRunRef,
					setError,
					handlers: {
						onComplete: () => {
							// Delayed refetch to catch background OpenAI config updates
							// (stream may close before background task completes)
							setTimeout(() => {
								const queryClient = getQueryClient();
								if (queryClient) {
									queryClient.invalidateQueries({ queryKey: ["agents"] });
									queryClient.invalidateQueries({ queryKey: ["tasks"] });
									queryClient.invalidateQueries({ queryKey: ["user-runs"] });
								}
							}, 5000);
						},
					},
				});
			} catch (err) {
				await handleStreamError(err, {
					dispatch,
					setError,
					setStreaming,
					runIdRef,
					removeLocalRun,
					pendingLocalRunRef,
					abortControllerRef,
				});
			}
		},
		[
			// Set user message
			dispatch,
			reset,
			setError,
			setStreaming,
			addLocalRun,
			removeLocalRun,
			setRunMetadata,
		]
	);

	const startChat = useCallback(
		async ({
			instanceId,
			message,
			runUuid,
			runId,
			accessToken,
			model,
			maxTurns = 200,
			permissionMode,
			integrationIds,
			files,
			historyBubbles,
			historyMetadata,
		}: ChatStreamOptions) => {
			if (!message.trim()) {
				throw new Error("Message is required");
			}
			if (!accessToken) {
				throw new Error("Not authenticated");
			}

			// Only reset if starting a NEW conversation
			// For follow-ups (runUuid exists), preserve history
			if (!runUuid && !runId) {
				reset();
				resetBlockIdSequence();
			} else if (historyBubbles && historyBubbles.length > 0) {
				const metadata = {
					runId: historyMetadata?.runId ?? runId,
					agentName: historyMetadata?.agentName,
					runUuid: historyMetadata?.runUuid ?? runUuid,
				};
				hydrate({ bubbles: historyBubbles, runMetadata: metadata });
			}
			setError(null);
			setStreaming(true);
			pendingLocalRunRef.current = true;
			isCleanedUpRef.current = false; // Reset cleanup flag for new stream

			// Set user message
			dispatch({ type: "START_USER_MESSAGE", message });

			// Store access token for cancellation
			accessTokenRef.current = accessToken;

			try {
				// Mock mode
				if (isMockMode) {
					const script = await loadMockScript(env.VITE_AGENT_STREAM_FIXTURE_URL);
					const mockResponse = createMockResponse(script);

					await runStream(mockResponse, {
						dispatch,
						addLocalRun,
						removeLocalRun,
						setStreaming,
						runIdRef,
						pendingLocalRunRef,
						setError,
					});
					return;
				}

				// Real streaming
				const abortController = new AbortController();
				abortControllerRef.current = abortController;

				let response: Response;

				// Use FormData with multipart when files are present
				if (files && files.length > 0) {
					const formData = new FormData();
					formData.append("task", message);
					formData.append("mode", "chat");
					formData.append("max_turns", String(maxTurns));
					formData.append("permission_mode", permissionMode ?? "yolo");
					if (model) formData.append("model", model);
					if (runUuid) formData.append("run_uuid", runUuid);
					if (runId) formData.append("run_id", String(runId));
					if (integrationIds) {
						for (const id of integrationIds) {
							formData.append("integration_ids", String(id));
						}
					}
					for (const file of files) {
						formData.append("files", file);
					}

					response = await fetch(
						`${env.VITE_API_BASE_URL}/api/v1/agents/instances/${instanceId}/execute-with-files`,
						{
							method: "POST",
							headers: { Authorization: `Bearer ${accessToken}` },
							body: formData,
							signal: abortController.signal,
						}
					);
				} else {
					// Standard JSON request without files
					const body: {
						task: string;
						mode: "chat";
						model?: string;
						max_turns: number;
						run_uuid?: string;
						run_id?: number;
						permission_mode?: "yolo" | "safe" | "plan";
						integration_ids?: number[];
					} = {
						task: message,
						mode: "chat",
						max_turns: maxTurns,
						permission_mode: permissionMode ?? "yolo",
					};

					if (model) {
						body.model = model;
					}
					if (runUuid) {
						body.run_uuid = runUuid;
					}
					if (runId) {
						body.run_id = runId;
					}
					if (integrationIds !== undefined) {
						body.integration_ids = integrationIds;
					}

					response = await fetch(
						`${env.VITE_API_BASE_URL}/api/v1/agents/instances/${instanceId}/execute`,
						{
							method: "POST",
							headers: {
								"Content-Type": "application/json",
								Authorization: `Bearer ${accessToken}`,
							},
							body: JSON.stringify(body),
							signal: abortController.signal,
						}
					);
				}

				await throwIfNotOk(response);

				await runStream(response, {
					dispatch,
					addLocalRun,
					removeLocalRun,
					setStreaming,
					runIdRef,
					pendingLocalRunRef,
					setError,
					handlers: {
						onComplete: () => {
							setTimeout(() => {
								const queryClient = getQueryClient();
								if (queryClient) {
									queryClient.invalidateQueries({ queryKey: ["agents"] });
									queryClient.invalidateQueries({ queryKey: ["tasks"] });
									queryClient.invalidateQueries({ queryKey: ["user-runs"] });
								}
							}, 5000);
						},
					},
				});
			} catch (err) {
				await handleStreamError(err, {
					dispatch,
					setError,
					setStreaming,
					runIdRef,
					removeLocalRun,
					pendingLocalRunRef,
					abortControllerRef,
				});
			}
		},
		[
			// Set user message
			dispatch,
			reset,
			setError,
			setStreaming,
			hydrate,
			addLocalRun,
			removeLocalRun,
		]
	);

	const stop = useCallback(
		async ({ cancelRun = true }: { cancelRun?: boolean } = {}) => {
			// Fix 7: Prevent double cleanup
			if (isCleanedUpRef.current || !isStreaming) {
				return;
			}
			isCleanedUpRef.current = true;

			const runId = runIdRef.current;
			const accessToken = accessTokenRef.current;
			const abortController = abortControllerRef.current;

			// Call backend cancel endpoint if we have run_id and token
			if (cancelRun && runId && accessToken) {
				try {
					await fetch(`${env.VITE_API_BASE_URL}/api/v1/tasks/runs/${runId}/cancel`, {
						method: "POST",
						headers: {
							Authorization: `Bearer ${accessToken}`,
						},
					});
				} catch (err) {
					console.error("Failed to cancel run on backend:", err);
				}
			}

			// Abort the fetch request and clear ref (Fix 1)
			if (abortController) {
				abortController.abort();
				abortControllerRef.current = null;
			}

			setStreaming(false);
			pendingLocalRunRef.current = false;
			if (runId) {
				removeLocalRun(runId);
			}

			// Clear metadata to prevent stale state affecting follow-up flows
			setRunMetadata({});
		},
		[isStreaming, setStreaming, removeLocalRun, setRunMetadata]
	);

	const join = useCallback(
		async ({ runId, accessToken, historyBubbles = [], historyMetadata }: JoinStreamOptions) => {
			if (!runId) {
				throw new Error("runId is required to join a stream");
			}
			if (!accessToken) {
				throw new Error("Not authenticated");
			}

			if (isStreaming && runMetadata.runId === runId) {
				return;
			}

			if (isStreaming && runMetadata.runId && runMetadata.runId !== runId) {
				await stop({ cancelRun: false });
			}

			resetBlockIdSequence();
			hydrate({
				bubbles: historyBubbles,
				runMetadata: {
					runId,
					agentName: historyMetadata?.agentName,
					runUuid: historyMetadata?.runUuid,
				},
			});

			setError(null);
			setStreaming(true);
			pendingLocalRunRef.current = true;
			isCleanedUpRef.current = false; // Reset cleanup flag for new stream
			accessTokenRef.current = accessToken;

			const abortController = new AbortController();
			abortControllerRef.current = abortController;

			try {
				const response = await fetch(`${env.VITE_API_BASE_URL}/api/v1/runs/${runId}/stream`, {
					method: "GET",
					headers: {
						Authorization: `Bearer ${accessToken}`,
						Accept: "text/event-stream",
					},
					signal: abortController.signal,
				});

				await throwIfNotOk(response);

				await runStream(response, {
					dispatch,
					addLocalRun,
					removeLocalRun,
					setStreaming,
					runIdRef,
					pendingLocalRunRef,
					setError,
					handlers: {
						onMetadata: (rid) => {
							if (rid) {
								addLocalRun(rid);
								pendingLocalRunRef.current = false;
							}
						},
						onComplete: () => {
							setStreaming(false);
						},
					},
				});
			} catch (err) {
				await handleStreamError(err, {
					dispatch,
					setError,
					setStreaming,
					runIdRef,
					removeLocalRun,
					pendingLocalRunRef,
					abortControllerRef,
				});
			}
		},
		[
			dispatch,
			hydrate,
			isStreaming,
			runMetadata.runId,
			setError,
			setStreaming,
			stop,
			addLocalRun,
			removeLocalRun,
		]
	);

	const resetStream = useCallback(() => {
		void stop({ cancelRun: false });
		reset();
		resetBlockIdSequence();
	}, [stop, reset]);

	return {
		bubbles,
		agentName: runMetadata.agentName || null,
		runUuid: runMetadata.runUuid || null,
		runId: runMetadata.runId || null,
		isStreaming,
		error,
		start,
		startChat,
		join,
		stop,
		reset: resetStream,
	};
}
